/**
 * 템플릿 → 한 장짜리 HTML(2026-10-30) — 템플릿을 [화면 편집] 으로 고칠 때 쓴다.
 *
 * 템플릿은 정해진 디자인의 칸을 채우는 틀이라 요소를 옮기고 크기를 바꾼 결과를 담을 자리가 없다. 그래서 **그 순간의 모습을
 * HTML 로 옮긴 사본**을 고치고, 사본은 올린 HTML 콘텐츠가 된다(원래 템플릿은 그대로 남는다). 화면은 그것을 올린 HTML 처럼
 * 샌드박스로 띄운다 — 에이전트 0.7.17 이상이면 다 된다.
 *
 * 사본은 서버·인터넷 없이 혼자 서야 한다(화면은 HTML 안에서 https·data: 밖으로 나가지 못한다). 그래서 템플릿 글꼴·사내 글꼴·
 * 로고·반반 화면의 이미지를 `data:` 로 품는다(영상은 콘텐츠를 가리킨다 — `media`). 시계와 남은 시간은 옮긴 순간에 멈추지 않게 작은 스크립트가 초마다 고쳐 쓴다(화면의 HTML 은
 * 스크립트가 돈다). 그 스크립트는 안드로이드 5.1 웹뷰(Chromium 39)에서도 돌게 ES5 로 쓴다.
 */
import {
  BRAND_FONT_ID,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  DEFAULT_TEMPLATE_FONT,
  TEMPLATE_CSS,
  TEMPLATE_FONTS,
  brandFontFaceCss,
  renderTemplateStage,
  templateBrandCss,
  templateFontFaceCss,
  VIDEO_ATTR,
  ZERO_ACCIDENT_MULTIPLIERS,
  type TemplatePayload,
} from '@signageone/core';
import { fitWithin } from './model.js';

export interface TemplateHtmlInput {
  payload: TemplatePayload;
  now: Date;
  /** 시계를 맞출 시간대 — 고객사 시간대. */
  timezone: string;
  /** 머리글의 장소. 사본은 어느 화면에나 같은 글이 나가므로 비워 두는 것이 기본이다. */
  location: string;
  brand: {
    name: string;
    logoDataUrl: string | null;
    accent: string | null;
    font: { dataUrl: string; mime: string } | null;
  };
  /** 이 템플릿이 쓰는 글꼴 파일 이름 → `data:` 주소(`templateFontFiles`). 모르는 파일은 물러날 글꼴로 그려진다. */
  fontData: Readonly<Record<string, string>>;
  /**
   * 반반 화면의 그림·영상(2026-10-30) — 이미지는 자산 id → `data:` 주소(사본에 품는다), 영상은 `templateVideoRef(id)`. 영상은
   * 품지 않고 [화면 편집] 의 영상과 같은 약속(`data-so-video`)으로 콘텐츠를 가리킨다 — 화면은 칸 밖 겹층으로 튼다.
   */
  media?: Readonly<Record<string, string>>;
}

const VIDEO_REF_PREFIX = 'so-video:';

/** 사본의 영상 자리 표시 — 렌더러가 `src` 에 적은 것을 `data-so-video` 로 바꾼다(`templateHtmlDocument`). */
export const templateVideoRef = (assetId: string): string => `${VIDEO_REF_PREFIX}${assetId}`;

/**
 * 이 템플릿이 쓰는 글꼴 파일 — 사본에 품을 것만 받는다(글꼴 한 벌이 수백 KB 다). 사내 글꼴을 고른 템플릿은 그 글꼴을
 * 따로 품고, 물러날 곳인 기본 글꼴을 받는다(`brandFontStack`).
 */
export function templateFontFiles(payload: Pick<TemplatePayload, 'font'>): string[] {
  const id = !payload.font || payload.font === BRAND_FONT_ID ? DEFAULT_TEMPLATE_FONT : payload.font;
  return TEMPLATE_FONTS[id].faces.map((face) => face.file);
}

/** 사본의 기준 크기 — 가로 캔버스 그대로. */
export const TEMPLATE_HTML_SIZE = { width: CANVAS_WIDTH, height: CANVAS_HEIGHT } as const;

/** 무재해 현황의 시작일·배수 — 사본의 스크립트가 날마다 다시 센다. */
export interface ZeroAccidentLive {
  startDate: string;
  multiplier: string;
}

/**
 * 시계·남은 시간을 살려 두는 스크립트(ES5). 템플릿 렌더러와 같은 글 모양으로 쓴다(`render.ts` 의 `formatClock`) —
 * `2026년 10월 30일 목요일` · `14:05`. 마감이 있으면 `.so-promo__countdown-value` 넷(일·시·분·초)을 고친다.
 * 무재해 현황이면 `data-zero` 칸(D-day·숫자·문장·막대)을 그날 날짜로 다시 센다 — 셈은 `render.ts` 의 `zeroAccidentFigures`
 * 와 같아야 한다(시험이 두 셈을 맞춰 본다). 시간대를 모르는 옛 웹뷰는 기기 시각으로 그린다.
 */
export function liveClockScript(timezone: string, deadline: Date | null, zero: ZeroAccidentLive | null = null): string {
  return (
    '(function(){' +
    `var tz=${JSON.stringify(timezone)};var deadline=${deadline ? String(deadline.getTime()) : 'null'};` +
    `var zero=${zero ? JSON.stringify({ s: zero.startDate, m: zero.multiplier }) : 'null'};` +
    `var mults=${JSON.stringify(ZERO_ACCIDENT_MULTIPLIERS)};` +
    "var days=['일','월','화','수','목','금','토'];" +
    "function pad(n){return (n<10?'0':'')+n;}" +
    'function zoned(now){var s=null;' +
    "try{s=now.toLocaleString('en-US',{timeZone:tz,hour12:false,year:'numeric',month:'numeric',day:'numeric',hour:'numeric',minute:'numeric'});}catch(e){s=null;}" +
    'var m=s?/(\\d+)\\/(\\d+)\\/(\\d+),?\\s+(\\d+):(\\d+)/.exec(s):null;' +
    'if(!m){return {y:now.getFullYear(),mo:now.getMonth()+1,d:now.getDate(),h:now.getHours(),mi:now.getMinutes()};}' +
    'var h=+m[4];if(h===24){h=0;}return {y:+m[3],mo:+m[1],d:+m[2],h:h,mi:+m[5]};}' +
    'function put(sel,text){var list=document.querySelectorAll(sel);for(var i=0;i<list.length;i++){if(list[i].textContent!==text){list[i].textContent=text;}}}' +
    'function tick(){var now=new Date();var z=zoned(now);var wd=new Date(Date.UTC(z.y,z.mo-1,z.d)).getUTCDay();' +
    "put('.so-tpl__date',z.y+'년 '+z.mo+'월 '+z.d+'일 '+days[wd]+'요일');put('.so-tpl__time',pad(z.h)+':'+pad(z.mi));" +
    'if(deadline!==null){var t=Math.max(0,Math.floor((deadline-now.getTime())/1000));' +
    "var v=[Math.floor(t/86400),Math.floor((t%86400)/3600),Math.floor((t%3600)/60),t%60];var cells=document.querySelectorAll('.so-promo__countdown-value');" +
    'for(var i=0;i<cells.length&&i<4;i++){var text=pad(v[i]);if(cells[i].textContent!==text){cells[i].textContent=text;}}}' +
    'if(zero!==null){zeroTick(z);}}' +
    'function dix(y,mo,d){return Date.UTC(y,mo-1,d)/86400000;}' +
    "function dot(i){var t=new Date(i*86400000);return t.getUTCFullYear()+'.'+pad(t.getUTCMonth()+1)+'.'+pad(t.getUTCDate());}" +
    "function commas(n){return String(n).replace(/\\B(?=(\\d{3})+(?!\\d))/g,',');}" +
    'function zeroTick(z){var p=zero.s.split(\'-\');var y=+p[0],mo=+p[1],d=+p[2];var at=-1;' +
    'for(var k=0;k<mults.length;k++){if(mults[k]===zero.m){at=k;}}' +
    'var mult=at>=0?+zero.m:1;var prev=at>0?+mults[at-1]:0;' +
    'var base=dix(y,mo,d),ps=dix(y+prev,mo,d),end=dix(y+mult,mo,d)-1,now=dix(z.y,z.mo,z.d);' +
    'var total=end-ps+1;var el=Math.min(total,Math.max(0,now-ps+1));var rem=total-el;var pct=Math.min(100,Math.max(0,el/total*100));' +
    "var phase=now<ps?'before':now>end?'done':rem===0?'today':'running';" +
    "var dday=phase==='before'?'D-'+(ps-now):phase==='done'?'D+'+(now-end):phase==='today'?'D-DAY':'D-'+rem;" +
    "var m=mult+'배수';var label=m+' 달성까지',status=m+' 목표 달성까지 '+commas(rem)+'일 남았습니다';" +
    "if(phase==='before'){label=m+' 기간 시작까지';status=m+' 목표 기간 시작까지 '+commas(ps-now)+'일 남았습니다';}" +
    "if(phase==='today'){label=m+' 달성';status='오늘 '+m+' 무재해 목표를 달성합니다';}" +
    "if(phase==='done'){label=m+' 달성';status=m+' 무재해 목표를 달성했습니다';}" +
    "function zput(key,text){put('[data-zero=\"'+key+'\"]',text);}" +
    "zput('dday',dday);zput('label',label);zput('status',status);zput('multiplier',String(mult));zput('end',dot(end));" +
    "zput('total',commas(total));zput('elapsed',commas(el));zput('percent',pct.toFixed(1));zput('cumulative',commas(Math.max(0,now-base+1)));" +
    "zput('period',dot(ps)+' ~ '+dot(end));" +
    "var bars=document.querySelectorAll('[data-zero=\"bar\"]');for(var b=0;b<bars.length;b++){bars[b].style.width=pct+'%';}" +
    "var boxes=document.querySelectorAll('[data-zero-phase]');for(var q=0;q<boxes.length;q++){var box=boxes[q];" +
    "if(box.getAttribute('data-zero-phase')!==phase){box.className=box.className.replace(/so-zero__dday--[a-z]+/,'so-zero__dday--'+phase);" +
    "box.setAttribute('data-zero-phase',phase);}}}" +
    'tick();setInterval(tick,1000);})();'
  );
}

/**
 * 레이어 칸에 보일 이름 — 사본의 뼈대 부분에 `data-name` 을 붙인다(`layerName` 이 먼저 본다). 클래스 이름(`so-tpl__header`)은
 * 비전문가가 읽을 수 없다. 글 자체인 요소에는 붙이지 않는다 — 피그마처럼 그 글이 이름으로 보이는 편이 낫다.
 */
const PART_NAMES: Readonly<Record<string, string>> = {
  'so-tpl-stage': '템플릿 무대',
  'so-tpl-canvas': '템플릿 화면',
  'so-tpl': '템플릿',
  'so-body': '본문 칸',
  'so-card': '카드',
  'so-col': '칸',
  'so-tile-icon': '아이콘',
  'so-ann__main': '공지 카드',
  'so-ann__side': '오른쪽 칸',
  'so-ann__notices': '최근 공지',
  'so-promo__visual': '홍보 칸',
  'so-promo__side': '오른쪽 칸',
  'so-promo__amount': '금액',
  'so-tpl__header': '머리글',
  'so-tpl__brand': '회사 이름·로고',
  'so-tpl__brand-logo': '로고',
  'so-tpl__clock': '날짜·시계',
  'so-tpl__main': '본문',
  'so-tpl__footer': '하단 띠',
  'so-tpl__ticker': '흐르는 글',
  'so-tpl__ticker-track': '흐르는 글 칸',
  'so-tpl__live': '실시간 표시',
  'so-badge': '배지',
  'so-ann__body': '공지 본문',
  'so-ann__meta': '날짜·작성 부서',
  'so-ann__notice-list': '최근 공지 목록',
  'so-ann__notice': '공지 한 줄',
  'so-ann__weather': '날씨',
  'so-promo__countdown': '남은 시간',
  'so-promo__countdown-row': '남은 시간 숫자',
  'so-promo__benefits': '혜택 목록',
  'so-promo__benefit': '혜택',
  'so-promo__qr': 'QR',
  'so-alert__head': '알림 머리',
  'so-alert__icon': '알림 아이콘',
  'so-alert__eyebrow': '알림 이름표',
  'so-alert__actions': '행동 요령',
  'so-alert__action': '행동 요령 한 줄',
  'so-alert__stats': '수치',
  'so-alert__stat': '수치 한 칸',
  'so-dash__kpis': '핵심 지표',
  'so-dash__charts': '차트',
  'so-dash__bars': '막대 차트',
  'so-dash__legend': '범례',
  'so-dash__rows': '순위',
  'so-dash__row': '순위 한 줄',
  'so-sched__date': '오늘 날짜',
  'so-sched__events': '일정 목록',
  'so-sched__event': '일정',
  'so-sched__countdown': '남은 시간',
  'so-sched__stats-grid': '통계',
  'so-sched__upcoming-item': '다가오는 일정',
  'so-split__media': '그림 칸',
  'so-split__placeholder': '장식 무늬',
  'so-split__text': '글 카드',
  'so-split__copy': '제목·설명',
  'so-split__aside': '핵심 항목·아래 한 줄',
  'so-split__points': '핵심 항목',
  'so-split__point': '핵심 항목 한 줄',
  'so-split__note': '아래 한 줄',
  'so-zone--media': '그림 구역',
  'so-zone--text': '글 구역',
  'so-zone__slide': '한 장',
  'so-zone__title': '제목',
  'so-zone__lead': '설명',
  'so-zero__hero': '제목 카드',
  'so-zero__intro': '배지·제목·부서',
  'so-zero__meta': '부서·시작일·누적',
  'so-zero__dday': 'D-day',
  'so-zero__kpis': '숫자 다섯 칸',
  'so-zero__kpi': '숫자 칸',
  'so-zero__progress': '목표 진행률',
  'so-zero__track': '진행 막대',
};

export function nameTemplateParts(html: string): string {
  return html.replace(
    /<([a-z][a-z0-9]*)(\s[^>]*?)?\sclass="([^"]*)"/g,
    (match, tag: string, before: string | undefined, classes: string) => {
      if (before?.includes('data-name=')) return match;
      // 구체적인 부분 이름(`so-ann__main`)이 일반 이름(`so-card`)보다 먼저.
      const tokens = classes.split(/\s+/);
      const name = [...tokens.filter((token) => token.includes('__')), ...tokens]
        .map((token) => PART_NAMES[token])
        .find(Boolean);
      return name ? `<${tag}${before ?? ''} data-name="${name}" class="${classes}"` : match;
    },
  );
}

/** 템플릿의 그 순간 모습을 한 장짜리 HTML 로. */
export function templateHtmlDocument(input: TemplateHtmlInput): string {
  const { payload, brand } = input;
  const stage = nameTemplateParts(
    renderTemplateStage(
      payload,
      {
        location: input.location,
        now: input.now,
        timezone: input.timezone,
        brand: brand.name,
        logoUrl: brand.logoDataUrl,
        media: input.media,
      },
      TEMPLATE_HTML_SIZE,
    ),
  ).replace(/ src="so-video:([0-9a-f]{24})"/g, (_, id: string) => ` ${VIDEO_ATTR}="${id}"`);
  const fonts = templateFontFaceCss((file) => input.fontData[file]);
  const deadline = payload.id === 'promotion' ? (payload.data.deadline ?? null) : null;
  const zero = payload.id === 'safety' ? { startDate: payload.data.startDate, multiplier: payload.data.multiplier } : null;
  return (
    '<!doctype html>\n<html lang="ko"><head><meta charset="utf-8">' +
    `<meta name="viewport" content="width=${CANVAS_WIDTH}, initial-scale=1">` +
    `<style>html,body{margin:0;padding:0;overflow:hidden;background:#000;width:${CANVAS_WIDTH}px;height:${CANVAS_HEIGHT}px}\n${fonts}\n${TEMPLATE_CSS}</style>` +
    `<style>${brand.font ? brandFontFaceCss(brand.font.dataUrl, brand.font.mime) : ''}\n${templateBrandCss(brand.accent)}</style>` +
    `</head><body>${stage}<script>${liveClockScript(input.timezone, deadline, zero)}</script></body></html>`
  );
}

/** 주소의 파일을 `data:` 로 — 사본이 혼자 서게. 못 읽으면 null(그 조각만 빠진다). */
export async function toDataUrl(url: string | null | undefined, fetcher: typeof fetch = fetch): Promise<string | null> {
  if (!url) return null;
  try {
    const response = await fetcher(url, { credentials: 'include' });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * 반반 화면의 이미지를 사본에 품을 `data:` 로 — 긴 변 2048px 로 줄인다([넣기] ▸ [그림] 과 같은 기준, 올린 HTML 상한 10MB).
 * 못 읽으면 null(그 자리는 장식 무늬).
 */
export async function imageDataUrl(url: string | null | undefined, maxSide = 2048): Promise<string | null> {
  const original = await toDataUrl(url);
  if (!original) return null;
  return new Promise<string | null>((resolve) => {
    const probe = new Image();
    probe.onerror = () => resolve(null);
    probe.onload = () => {
      const width = probe.naturalWidth || 1;
      const height = probe.naturalHeight || 1;
      const size = fitWithin(width, height, maxSide);
      if (size.width >= width && original.length < 1.5 * 1024 * 1024) {
        resolve(original);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = size.width;
      canvas.height = size.height;
      canvas.getContext('2d')?.drawImage(probe, 0, 0, size.width, size.height);
      resolve(original.startsWith('data:image/png') ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.86));
    };
    probe.src = original;
  });
}
